import { createApi } from '@reduxjs/toolkit/query/react';
import { Language } from '@shared/enum';
import { apiBaseQuery } from '@shared/functions';

export enum UserRole {
    DRIVER = 'driver',
    INSTRUCTOR = 'instructor',
    SYSTEM_ADMINISTRATOR = 'system_administrator',
}

export type ErrorObject = {
    [key: string]: string;
};

export interface UserLoginRequest {
    username: string;
    password: string;
    language: Language;
    rememberMe: boolean;
}

export interface UserLoginResponse {
    status: number;
    message: string;
    data: unknown;
    error: ErrorObject;
}

export interface ValidateTokenRequest {
    language: Language;
}

export interface ValidateTokenResponse {
    status: number;
    message: string;
    data: {
        isValid: boolean;
        isRenewed: boolean;
    };
    error: ErrorObject;
}

export interface LogoutRequest {
    language: Language;
}

export interface LogoutResponse {
    status: number;
    message: string;
    data: unknown;
    error: ErrorObject;
}

export interface ResetRequest {
    language: Language;
    email: string;
}

export interface ResetResponse {
    status: number;
    message: string;
    data: unknown;
    error: ErrorObject;
}

export interface ChangePasswordRequest {
    language: Language;
    password: string;
    token: string;
}
export interface ChangePasswordResponse {
    status: number;
    message: string;
    data: unknown;
    error: ErrorObject;
}

export interface UserBaseInfoRequest {
    language: Language;
}

export interface UserBaseInfoResponse {
    status: number;
    message: string;
    data: {
        full_name: string;
        language: string;
        role_names: UserRole;
    };
    error: ErrorObject;
}

export interface UsersRequest {
    language: Language;
}

export interface User {
    user_id: number;
    full_name: string;
    login: string;
    group_id: number[];
}

export interface UserMeta {
    id: number;
    name: string;
    groupsIds: number[];
    email?: string;
}

export interface Group {
    group_id: number;
    name: string;
}

export interface GroupMeta {
    id: number;
    name: string;
}

export interface UsersResponse {
    status: number;
    message: string;
    data: {
        users: User[];
        groups: Group[];
    };
    error: ErrorObject;
}

export interface UserSettings {
    first_name: string;
    last_name: string;
    login: string;
    tacho_card_number?: string | null;
    phone_number?: string | null;
    reminder: boolean;
    marketing_message: boolean;
    password_updated_at?: string | null;
    company_names: string;
    role_names: string;
    language_name: string;
    iso_code: string;
}

export interface UserSettingsRequest {
    language: Language;
}

export interface UserSettingsResponse {
    status: number;
    message: string;
    data: UserSettings;
    error: ErrorObject;
}

export interface UpdateUserSettingsRequest {
    language: Language;
    marketingMessages: boolean;
    reminders: boolean;
}

export interface UpdateUserSettingsResponse {
    status: number;
    message: string;
    data: unknown;
    error: ErrorObject;
}

export interface UpdateUserPasswordRequest {
    language: Language;
    password: string;
    oldPassword: string;
}

export interface UpdateUserPasswordResponse {
    status: number;
    message: string;
    data: unknown;
    error: ErrorObject;
}

export interface UserProfile {
    user_id: number;
    login: string;
    first_name: string;
    last_name: string;
    driver_license: string | null;
    is_enabled: boolean;
    created_at: string;
    language_id: number | null;
    company_name: string;
    group_ids: number[];
}

export interface ListUsersRequest {
    language: Language;
    page: number;
    pageSize: number;
    search?: string;
}

export interface ListUsersResponse {
    status: number;
    message: string;
    data: {
        users: UserProfile[];
        total_count: number;
        page: number;
        page_size: number;
    };
    error: ErrorObject;
}

export interface GetUserRequest {
    language: Language;
    user_id: number;
}

export interface GetUserResponse {
    status: number;
    message: string;
    data: UserProfile;
    error: ErrorObject;
}

export interface RemoveFromUIRequest {
    language: Language;
    user_id: number;
}

export interface RemoveFromUIResponse {
    status: number;
    message: string;
    data: unknown;
    error: ErrorObject;
}

export interface UpdateUserRequest {
    language: Language;
    user_id: number;
    first_name: string;
    last_name: string;
    login: string;
    driver_license: string | null;
    is_enabled: boolean;
    language_id: number;
    group_ids: number[];
    password?: string | null;
}

export interface UpdateUserResponse {
    status: number;
    message: string;
    data: unknown;
    error: ErrorObject;
}

export interface ToggleUserStatusRequest {
    language: Language;
    user_id: number;
}

export interface ToggleUserStatusResponse {
    status: number;
    message: string;
    data: {
        is_enabled: boolean;
    };
    error: ErrorObject;
}

export interface SendResetRequest {
    language: Language;
    user_id: number;
}

export interface SendResetResponse {
    status: number;
    message: string;
    data: unknown;
    error: ErrorObject;
}

export interface CreateUserRequest {
    language: Language;
    login: string;
    first_name: string;
    last_name: string;
    driver_license?: string | null;
    language_id: number;
    is_enabled: boolean;
    password: string;
    group_ids: number[];
}

export interface CreateUserResponse {
    status: number;
    message: string;
    data: {
        user_id: number;
    };
    error: ErrorObject | Record<string, string>;
}

export interface BulkImportValidationRow {
    row_number: number;
    email: string;
    first_name: string;
    last_name: string;
    driver_license: string | null;
    language: string | null;
    is_enabled: boolean;
    groups: string[] | null;
    errors: string[];
}

export interface BulkImportValidationResponse {
    status: number;
    message: string;
    data: {
        total: number;
        valid: number;
        invalid: number;
        rows: BulkImportValidationRow[];
    };
    error: ErrorObject;
}

export interface BulkImportResultResponse {
    status: number;
    message: string;
    data: {
        created_count: number;
        failed_count: number;
        failed_rows: number[];
        validation: {
            total: number;
            valid: number;
            invalid: number;
            rows: BulkImportValidationRow[];
        };
    };
    error: ErrorObject;
}

export const userApi = createApi({
    reducerPath: 'userApi',
    baseQuery: apiBaseQuery,
    tagTypes: ['UserBaseInfo', 'UserSettings', 'Users'],
    endpoints: (builder) => ({
        login: builder.mutation<UserLoginResponse, UserLoginRequest>({
            query: (data) => ({
                url: 'v1/user/login',
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                    rememberMe: data.rememberMe,
                },
                headers: {
                    'X-RM-LOGIN': data.username,
                    'X-RM-PASSWORD': data.password,
                },
            }),
        }),
        validateToken: builder.mutation<ValidateTokenResponse, ValidateTokenRequest>({
            query: (data) => ({
                url: 'v1/user/validate_token',
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                },
            }),
        }),
        logout: builder.mutation<LogoutResponse, LogoutRequest>({
            query: (data) => ({
                url: 'v1/user/logout',
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                },
            }),
        }),
        reset: builder.mutation<ResetResponse, ResetRequest>({
            query: (data) => ({
                url: 'v1/user/reset',
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                    email: data.email,
                },
            }),
        }),
        changePassword: builder.mutation<ChangePasswordResponse, ChangePasswordRequest>({
            query: (data) => ({
                url: 'v1/user/change',
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                    password: data.password,
                    token: data.token,
                },
            }),
        }),
        getUserBaseInfo: builder.query<UserBaseInfoResponse, UserBaseInfoRequest>({
            query: (data) => ({
                url: `v1/user/retrieve_base_user_info?language=${encodeURIComponent(data.language.toLowerCase())}`,
                method: 'GET',
                credentials: 'include',
            }),
            providesTags: ['UserBaseInfo'],
        }),
        getUserSettings: builder.query<UserSettingsResponse, UserSettingsRequest>({
            query: (data) => ({
                url: `v1/user/retrieve_user_settings?language=${encodeURIComponent(data.language.toLowerCase())}`,
                method: 'GET',
                credentials: 'include',
            }),
            providesTags: ['UserSettings'],
        }),
        updateUserSettings: builder.mutation<UpdateUserSettingsResponse, UpdateUserSettingsRequest>({
            query: (data) => ({
                url: 'v1/user/update_user_settings',
                method: 'POST',
                credentials: 'include',
                body: {
                    marketingMessages: data.marketingMessages,
                    reminders: data.reminders,
                    language: data.language.toLowerCase(),
                },
            }),
            invalidatesTags: ['UserSettings'],
        }),
        updatePassword: builder.mutation<UpdateUserPasswordResponse, UpdateUserPasswordRequest>({
            query: (data) => ({
                url: 'v1/user/update_password',
                method: 'POST',
                credentials: 'include',
                body: {
                    oldPassword: data.oldPassword,
                    password: data.password,
                    language: data.language.toLowerCase(),
                },
            }),
        }),
        getUsers: builder.query<UsersResponse, UsersRequest>({
            query: (data) => ({
                url: `v1/user/retrieve_groups?language=${encodeURIComponent(data.language.toLowerCase())}`,
                method: 'GET',
                credentials: 'include',
            }),
            providesTags: ['Users'],
        }),
        listUsers: builder.query<ListUsersResponse, ListUsersRequest>({
            query: (data) => ({
                url: `v1/user/list_users?language=${encodeURIComponent(data.language.toLowerCase())}&page=${data.page}&pageSize=${data.pageSize}${data.search ? `&search=${encodeURIComponent(data.search)}` : ''}`,
                method: 'GET',
                credentials: 'include',
            }),
            providesTags: ['Users'],
        }),
        getUser: builder.query<GetUserResponse, GetUserRequest>({
            query: (data) => ({
                url: `v1/user/get_user?language=${encodeURIComponent(data.language.toLowerCase())}&user_id=${data.user_id}`,
                method: 'GET',
                credentials: 'include',
            }),
            providesTags: (_result, _error, arg) => [{ type: 'Users', id: arg.user_id }],
        }),
        removeFromUI: builder.mutation<RemoveFromUIResponse, RemoveFromUIRequest>({
            query: (data) => ({
                url: 'v1/user/remove_from_ui',
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                    user_id: data.user_id,
                },
            }),
            invalidatesTags: ['Users'],
        }),
        updateUser: builder.mutation<UpdateUserResponse, UpdateUserRequest>({
            query: (data) => ({
                url: 'v1/user/update_user',
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                    user_id: data.user_id,
                    first_name: data.first_name,
                    last_name: data.last_name,
                    login: data.login,
                    driver_license: data.driver_license,
                    is_enabled: data.is_enabled,
                    language_id: data.language_id,
                    group_ids: data.group_ids,
                    ...(data.password ? { password: data.password } : {}),
                },
            }),
            invalidatesTags: ['Users'],
        }),
        toggleUserStatus: builder.mutation<ToggleUserStatusResponse, ToggleUserStatusRequest>({
            query: (data) => ({
                url: 'v1/user/toggle_user_status',
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                    user_id: data.user_id,
                },
            }),
            invalidatesTags: ['Users'],
        }),
        sendReset: builder.mutation<SendResetResponse, SendResetRequest>({
            query: (data) => ({
                url: 'v1/user/send_reset',
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                    user_id: data.user_id,
                },
            }),
        }),
        validateBulkImport: builder.mutation<BulkImportValidationResponse, { language: Language; file: File }>({
            query: ({ language, file }) => {
                const formData = new FormData();
                formData.append('file', file);
                return {
                    url: `v1/user/bulk_import?language=${encodeURIComponent(language.toLowerCase())}&dry_run=true`,
                    method: 'POST',
                    credentials: 'include',
                    body: formData,
                };
            },
        }),
        executeBulkImport: builder.mutation<BulkImportResultResponse, { language: Language; file: File }>({
            query: ({ language, file }) => {
                const formData = new FormData();
                formData.append('file', file);
                return {
                    url: `v1/user/bulk_import?language=${encodeURIComponent(language.toLowerCase())}&dry_run=false`,
                    method: 'POST',
                    credentials: 'include',
                    body: formData,
                };
            },
            invalidatesTags: ['Users'],
        }),
        createUser: builder.mutation<CreateUserResponse, CreateUserRequest>({
            query: (data) => ({
                url: 'v1/user/create_user',
                method: 'POST',
                credentials: 'include',
                body: {
                    language: data.language.toLowerCase(),
                    login: data.login,
                    first_name: data.first_name,
                    last_name: data.last_name,
                    driver_license: data.driver_license || null,
                    language_id: data.language_id,
                    is_enabled: data.is_enabled,
                    password: data.password,
                    group_ids: data.group_ids,
                },
            }),
            invalidatesTags: ['Users'],
        }),
    }),
});

export const {
    useLoginMutation,
    useValidateTokenMutation,
    useResetMutation,
    useLogoutMutation,
    useChangePasswordMutation,
    useGetUserBaseInfoQuery,
    useGetUserSettingsQuery,
    useLazyGetUserSettingsQuery,
    useUpdateUserSettingsMutation,
    useUpdatePasswordMutation,
    useGetUsersQuery,
    useListUsersQuery,
    useGetUserQuery,
    useRemoveFromUIMutation,
    useUpdateUserMutation,
    useToggleUserStatusMutation,
    useSendResetMutation,
    useValidateBulkImportMutation,
    useExecuteBulkImportMutation,
    useCreateUserMutation,
} = userApi;
